import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { receivingApi, lookupsApi, projectsApi, usersApi } from '@/lib/apiClient';
import { StateView, Button } from '@/components';
import { fmtDate } from '@/lib/dates';
import { ReceivingStatusBadge } from '../receiving/statusBadge';
import { OrderDrawer, TrackingCell, type ReceivingOrder } from '../receiving/ReceivingPage';

// Project identity needed to pre-scope new orders to this job and lock the
// drawer's project picker. Shares the workspace's ['project', id] cache.
interface ProjectRef { id: number; jobNumber: string; title: string }

// Reuse the RFI/Mobilization table look so this tab feels native in the
// workspace (the global Receiving page has its own grouped table styling).
const css = `
  .rcv-table { width: 100%; border-collapse: collapse; font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); }
  .rcv-table th {
    text-align: left; padding: 10px var(--ew-s-4); white-space: nowrap;
    font: var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption);
    text-transform: uppercase; color: var(--ew-n-500);
    border-bottom: 1px solid var(--ew-n-100);
  }
  .rcv-table td { padding: 11px var(--ew-s-4); border-bottom: 1px solid var(--ew-n-100); color: var(--ew-dark); vertical-align: top; }
  .rcv-table tr:last-child td { border-bottom: 0; }
  .rcv-muted { color: var(--ew-n-500); }
  .rcv-actions { display: flex; gap: var(--ew-s-2); justify-content: flex-end; white-space: nowrap; }
`;

export default function ReceivingTab({ projectId, locked = false }: { projectId: number; locked?: boolean }) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['receiving', projectId],
    queryFn: () => receivingApi.list({ projectId }) as Promise<ReceivingOrder[]>,
  });

  // Project identity to lock the drawer's project to this job. Shares the
  // warm workspace cache; the drawer falls back gracefully if not yet loaded.
  const projectQuery = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId) as Promise<ProjectRef>,
    enabled: Number.isFinite(projectId),
    staleTime: 60_000,
  });
  const thisProject: ProjectRef = {
    id: projectId,
    jobNumber: projectQuery.data?.jobNumber ?? '',
    title: projectQuery.data?.title ?? '',
  };

  // Lookups for the drawer (statuses + delivery methods). Long staleTime —
  // these rarely change. Same queries the global Receiving page uses.
  const { data: statuses } = useQuery({ queryKey: ['receiving-statuses'], queryFn: () => lookupsApi.listReceivingStatuses(), staleTime: 30 * 60 * 1000 });
  const { data: methods } = useQuery({ queryKey: ['receiving-methods'], queryFn: () => lookupsApi.listReceivingDeliveryMethods(), staleTime: 30 * 60 * 1000 });

  // Permission gating — mirrors the backend guard (POST /receiving needs
  // 'receiving.edit'); Admin short-circuits. Non-permitted users see the list
  // read-only (no create/edit actions that would 403).
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => usersApi.me(), staleTime: 5 * 60 * 1000 });
  // Activity-locked (SYSTAC / ClosedWon) projects can't take receiving orders
  // until a Sage job number lands — read-only regardless of permission.
  const canEdit = ((me?.roles.includes('Admin') ?? false) || (me?.permissions.includes('receiving.edit') ?? false)) && !locked;

  // Drawer state: { item: null } = new order; { item } = edit existing.
  const [drawer, setDrawer] = useState<{ item: ReceivingOrder | null } | null>(null);

  const onSaved = () => {
    qc.invalidateQueries({ queryKey: ['receiving', projectId] });
    qc.invalidateQueries({ queryKey: ['project-counts', projectId] });
    setDrawer(null);
  };

  return (
    <>
      <style>{css}</style>

      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 'var(--ew-s-4)' }}>
        {canEdit ? <button className="btn btn-primary" onClick={() => setDrawer({ item: null })}>+ New order</button> : null}
      </div>

      <div className="pw-card">
        <h2 className="pw-card-hd">Receiving</h2>
        <StateView query={query} empty="No receiving orders for this job yet.">
          {(orders) => (
            <div className="ew-table-scroll">
            <table className="rcv-table ew-cards">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Status</th>
                  <th>Vendor</th>
                  <th>Qty</th>
                  <th>Expected</th>
                  <th>Tracking</th>
                  {canEdit ? <th aria-label="Actions" /> : null}
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id}>
                    <td data-label="Item">{o.itemOrdered ?? o.title}</td>
                    <td data-label="Status"><ReceivingStatusBadge label={o.status.label} /></td>
                    <td className="rcv-muted" data-label="Vendor">{o.vendor ?? '—'}</td>
                    <td className="rcv-muted" data-label="Qty">{o.quantity ?? '—'}</td>
                    <td className="rcv-muted" data-label="Expected" style={{ whiteSpace: 'nowrap' }}>{fmtDate(o.expectedDeliveryDate)}</td>
                    <td data-label="Tracking"><TrackingCell order={o} /></td>
                    {canEdit ? (
                      <td>
                        <div className="rcv-actions">
                          <Button variant="secondary" size="sm" onClick={() => setDrawer({ item: o })}>Edit</Button>
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </StateView>
      </div>

      {drawer && (
        <OrderDrawer
          item={drawer.item}
          statuses={statuses ?? []}
          methods={methods ?? []}
          // New orders are pre-scoped + locked to this job; edits keep their own project.
          projects={[thisProject]}
          lockedProject={drawer.item ? undefined : thisProject}
          onClose={() => setDrawer(null)}
          onSaved={onSaved}
        />
      )}
    </>
  );
}
